// Content file for the portfolio
// This will be used to store all dynamic content for CMS integration

export interface PersonalInfo {
  name: string;
  fullName: string;
  tagline: string;
  location: string;
  email: string;
  bio: string;
  socialLinks: SocialLink[];
  funFacts: string[];
  // Empty: the résumé is uploaded from /admin and served from Netlify Blobs,
  // so the switch that turns it off actually withholds it. A copy in public/
  // would stay reachable at its own URL whatever the switch said.
  resumeUrl: string;
}

export interface SocialLink {
  name: string;
  url: string;
  icon: string;
}

export interface Skill {
  name: string;
  icon: string;
}

// Deliberately not split by medium: these platforms all cover both image and
// video generation, so tagging each with one discipline would misrepresent them.
export interface AiTool {
  name: string;
  url?: string;
  // Logo saved into public/images/tools/ rather than hotlinked — two devicon
  // URLs in the skills marquee had silently 404'd, and these platforms aren't
  // in any icon library, so a local copy is the only stable option.
  logo?: string;
}

// A single generated piece shown in the Explore page's opening gallery.
export interface AiGeneration {
  id: string;
  title: string;
  image: string;
  // Intrinsic pixels of the served file. The gallery sizes a frame by height
  // and lets width follow the picture, so without these a frame has no width
  // until its image decodes, and every offset measured before that is wrong.
  width: number;
  height: number;
  // Whether the rail on Explore carries it. Set on every one of these because
  // the rail carried all of them before there was anything else to carry, and
  // that should not change just because uploading became possible.
  featured?: boolean;
}

// A generated clip. Deliberately the same shape as AiGeneration plus the file
// itself, so the rail, the wall and the sharing all treat the two kinds alike
// rather than growing a second set of everything.
export interface AiClip {
  id: string;
  title: string;
  // The still shown before it plays, and what the wall lays out with.
  poster: string;
  video: string;
  // Intrinsic pixels of the poster, for the same reason the pictures carry
  // them: without a shape the frame has no width until the file decodes.
  width: number;
  height: number;
}

export interface Project {
  id: string;
  title: string;
  description: string;
  category: 'static' | 'dynamic' | 'web3' | 'package' | 'ui';
  demoUrl?: string;
  repoUrl?: string;
  featured: boolean;
  priority: number; // 1-10, where 10 is highest priority
  status: 'completed' | 'in-progress' | 'concept';
  // Optional: when there's no real screenshot, cards fall back to the build
  // type glyph rather than showing generic stock imagery.
  thumbnail?: string;
  // Whether this may appear in the home page's featured grid. Defaults to
  // true; set false to keep a project to the full /projects listing only.
  spotlight?: boolean;
  // What kind of build it is, surfaced as an icon rather than a text label.
  // Optional because it doesn't apply to everything (an npm package is
  // neither a web app nor a mobile one).
  type?: 'web' | 'app' | 'hybrid';
}

// Personal Information
export const personalInfo: PersonalInfo = {
  name: 'E Wordsworth',
  fullName: 'E. Wordsworth',
  tagline: 'I build things on the internet and occasionally wander down dangerous AI rabbit holes.',
  location: 'Lagos, Nigeria - Remote',
  email: 'wordsemman@gmail.com',
  bio: `Hi there! I’m Emmanuel. I build things for the web and spend an unhealthy amount of time exploring AI. What usually starts as curiosity often turns into experiments, prototypes, and sometimes full projects. I enjoy taking an idea, breaking it apart, and turning it into something real people can interact with.

Lately that curiosity has pulled me deeper into AI. Not just using it, but understanding what it might change about how we build and create.

Most days you’ll find me building, testing ideas, and occasionally disappearing into a rabbit hole that started with “let me just check something quickly`,
  socialLinks: [
    {
      name: 'Instagram',
      url: 'https://www.instagram.com/techprophett/',
      icon: 'instagram'
    },
    {
      name: 'X',
      url: 'https://x.com/techprophett',
      icon: 'x'
    },
    {
      name: 'Reddit',
      url: 'https://www.reddit.com/user/techprophett_',
      icon: 'reddit'
    },
    {
      // Named for what it is. A WhatsApp link usually means "message me", and
      // this one opens a channel to follow, which is a different offer.
      name: 'WhatsApp Channel',
      url: 'https://whatsapp.com/channel/0029Vb704oC89ino9QV0GB2g',
      icon: 'whatsapp'
    },
    {
      name: 'GitHub',
      url: 'https://github.com/ewordsworth',
      icon: 'github'
    }
  ],
  resumeUrl: '',
  funFacts: [
    "I have a habit of opening 'one quick tab' that turns into a 3 hour tech rabbit hole.",
    'I spend an unhealthy amount of time experimenting with AI tools and ideas.',
    'I enjoy turning strange ideas into prototypes just to see if they work.'
  ]};

// Skills
export const skills: Skill[] = [
  // Frontend
  { name: 'Vue.js', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg' },
  { name: 'React', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg' },
  { name: 'Tailwind CSS', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg' },
  { name: 'Nuxt.js', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nuxtjs/nuxtjs-original.svg' },
  { name: 'Quasar', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/quasar/quasar-original.svg' },
  { name: 'TypeScript', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg' },

  // Backend
  { name: 'Node.js', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg' },
  { name: 'Express', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/express/express-original.svg' },
  { name: 'Strapi', icon: 'https://cdn.simpleicons.org/strapi/4945FF' },
  { name: 'Firebase', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/firebase/firebase-original.svg' },
  { name: 'MongoDB', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mongodb/mongodb-original.svg' },

  // Other
  { name: 'AI APIs', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg' },
  { name: 'Web3', icon: 'https://cdn.simpleicons.org/ethereum/627EEA' },
  { name: 'NPM Packages', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/npm/npm-original-wordmark.svg' },
  { name: 'GSAP', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg' },
  { name: 'Claude', icon: '/images/skills/claude.svg' },
  { name: 'Codex', icon: '/images/skills/codex.svg' },
  { name: 'Git', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/git/git-original.svg' },
];

// Projects
export const projects: Project[] = [
  {
    id: 'liolang',
    title: 'LioLang',
    description: 'A language-learning app with no streaks, points, or lessons: you just text with an AI persona who lives her life in Spanish, and learning happens because you want to answer her.',
    category: 'dynamic',
    featured: true,
    priority: 10,
    status: 'completed',
    type: 'hybrid'
  },
  {
    id: 'wheyham',
    title: 'Wheyham',
    description: 'Digital sanctuary for African heritage, documenting 300 Nok artefacts with immersive exploration, a marketplace, and live auctions.',
    category: 'dynamic',
    demoUrl: 'https://wheyham.org',
    featured: true,
    priority: 10,
    status: 'completed',
    thumbnail: '/images/projects/wheyham.webp',
    type: 'web'
  },
  {
    id: 'noccrref',
    title: 'NOCCRREF',
    description: 'Site for the Northern Cultural and Creative Festival, promoting and preserving Northern Nigerian cultural heritage.',
    category: 'dynamic',
    demoUrl: 'https://noccrref.netlify.app',
    featured: true,
    priority: 10,
    status: 'completed',
    thumbnail: '/images/projects/noccrref.webp',
    type: 'web'
  },
  {
    id: 'itta',
    title: 'itta',
    description: 'Uber for fashion: a mobile-first Nigerian marketplace where clients post sew requests, tailors bid to win the job, and body measurements come from a single photo.',
    category: 'dynamic',
    demoUrl: 'https://app.itta.ng',
    featured: true,
    priority: 10,
    status: 'completed',
    type: 'web',
    thumbnail: '/images/projects/itta.webp'
  },
  {
    id: 'greysoft',
    title: 'Greysoft Technologies',
    description: 'Company site for Greysoft Technologies, an XR studio building immersive VR, MR, and AR experiences across education, health, defence, and real estate.',
    category: 'dynamic',
    demoUrl: 'https://www.greysoft.ng',
    featured: true,
    priority: 10,
    status: 'completed',
    thumbnail: '/images/projects/greysoft.webp',
    type: 'web'
  },
  {
    id: '11med',
    title: '11Med',
    description: 'Medication adherence and medical records for Nigerian patients, built for the caregivers who manage them too.',
    category: 'dynamic',
    featured: true,
    priority: 10,
    status: 'completed',
    type: 'app'
  },
  {
    id: 'carbon-curb',
    title: 'CarbonCurb',
    description: 'A gamified B2B platform that helps businesses cut their carbon footprint and bring employees into the process.',
    category: 'dynamic',
    featured: true,
    priority: 10,
    status: 'completed',
    type: 'hybrid'
  },
  {
    id: 'kachia-lga',
    title: 'Kachia Local Government',
    description: 'Official web presence for Kachia Local Government Area, Kaduna State.',
    category: 'dynamic',
    // demoUrl removed: kachialga.kd.gov.ng no longer resolves (DNS failure on
    // both http and https). The card now falls back to its type glyph and is
    // not clickable, rather than sending people to a dead host. Restore the URL
    // if the site comes back.
    featured: false,
    priority: 10,
    status: 'completed',
    spotlight: false,
    type: 'web'
  },
  {
    id: 'kaduna-agritech',
    title: 'Kaduna Agritech Conference',
    description: 'Event site for the Kaduna conference bringing together agricultural technology stakeholders.',
    category: 'static',
    demoUrl: 'https://agritech.greysoft.ng',
    featured: false,
    priority: 9,
    status: 'completed',
    thumbnail: '/images/projects/kaduna-agritech.webp',
    spotlight: false,
    type: 'web'
  },
  {
    id: 'buildpr',
    title: 'BuildPR',
    description: 'A web application built and deployed end to end.',
    category: 'dynamic',
    demoUrl: 'https://buildpr.onrender.com',
    featured: false,
    priority: 8,
    status: 'completed',
    thumbnail: '/images/projects/buildpr.webp',
    spotlight: false,
    type: 'web'
  },
  {
    id: 'minas-grill-house',
    title: "Mina's Grill House",
    description: 'Restaurant site presenting the menu and dining offering for a grill house.',
    category: 'static',
    demoUrl: 'https://miracle-lxf6.onrender.com',
    featured: false,
    priority: 8,
    status: 'completed',
    thumbnail: '/images/projects/minas-grill.webp',
    spotlight: false,
    type: 'web'
  },
  {
    id: 'hotel-brochure',
    title: 'Hotel Brochure',
    description: 'An elegant online brochure showcasing hotel amenities and rooms.',
    category: 'static',
    featured: false,
    priority: 7,
    status: 'completed',
    spotlight: false
  },
  {
    id: 'admin-dashboard',
    title: 'Admin Dashboard',
    description: 'A powerful dashboard for managing users, posts, and analytics.',
    category: 'dynamic',
    featured: true,
    priority: 10,
    status: 'completed',
    spotlight: false
  },
  {
    id: 'chat-app',
    title: 'Real-time Chat App',
    description: 'A real-time chat application with rooms and direct messaging.',
    category: 'dynamic',
    featured: true,
    priority: 9,
    status: 'completed',
    spotlight: false
  },
  {
    id: 'nft-gallery',
    title: 'NFT Gallery Viewer',
    description: 'A gallery to view and showcase NFT collections.',
    category: 'web3',
    featured: false,
    priority: 7,
    status: 'completed',
    spotlight: false
  },
  {
    id: 'color-palette',
    title: 'Color Palette Generator',
    description: 'An NPM package that generates harmonious color palettes.',
    category: 'package',
    featured: false,
    priority: 5,
    status: 'completed',
    spotlight: false
  },
  {
    id: 'vue-scaffold',
    title: 'Vue Project Scaffolder',
    description: 'A CLI tool to quickly scaffold Vue.js projects with best practices.',
    category: 'package',
    featured: false,
    priority: 4,
    status: 'completed',
    spotlight: false
  },
  {
    id: 'quasar-components',
    title: 'Quasar UI Components',
    description: 'A collection of reusable UI components for Quasar Framework.',
    category: 'ui',
    featured: false,
    priority: 3,
    status: 'completed',
    spotlight: false
  },
];

// Generative-AI tools used regularly. `url` is intentionally omitted for now —
// see the note on the AiTool interface.
export const aiTools: AiTool[] = [
  { name: 'Higgsfield', url: 'https://higgsfield.ai', logo: '/images/tools/higgsfield.png' },
  { name: 'Kling', url: 'https://kling.ai', logo: '/images/tools/kling.png' },
  { name: 'ImagineArt', url: 'https://imagine.art', logo: '/images/tools/imagineart.png' },
  { name: 'OpenArt', url: 'https://openart.ai', logo: '/images/tools/openart.png' },
  { name: 'SeaArt', url: 'https://seaart.ai', logo: '/images/tools/seaart.png' },
  { name: 'YouArt', url: 'https://youart.ai', logo: '/images/tools/youart.svg' },
  { name: 'Yapper', url: 'https://yapper.so', logo: '/images/tools/yapper.png' },
];

// The real renders, curated and named. Ordered roughly by how well each one
// shows off the work: the home strip takes the first six, the /explore
// gallery runs the lot.
//
// `tool` is intentionally empty: which platform produced which piece is not
// something that can be read off the files, and guessing would be inventing
// a claim about your own work. The caption is hidden while it is blank.
export const aiGenerations: AiGeneration[] = [
  { id: 'ethereal', title: 'Ethereal', image: '/images/ai/ethereal.webp', width: 558, height: 1000, featured: true },
  { id: 'woman-gold', title: 'Woman in Gold', image: '/images/ai/woman-gold.webp', width: 667, height: 1000, featured: true },
  { id: 'dune-woman', title: 'Dune Woman', image: '/images/ai/dune-woman.webp', width: 667, height: 1000, featured: true },
  { id: 'golden-floating-metropolis-1', title: 'Golden Floating Metropolis I', image: '/images/ai/golden-floating-metropolis-1.webp', width: 667, height: 1000, featured: true },
  { id: 'golden-floating-metropolis-2', title: 'Golden Floating Metropolis II', image: '/images/ai/golden-floating-metropolis-2.webp', width: 667, height: 1000, featured: true },
  { id: 'wyrlock-wirebound', title: 'Wyrlock Wirebound', image: '/images/ai/wyrlock-wirebound.webp', width: 667, height: 1000, featured: true },
  { id: 'wyrlock-03', title: 'Wyrlock 03', image: '/images/ai/wyrlock-03.webp', width: 667, height: 1000, featured: true },
  { id: 'wyrlock-cat', title: 'Wyrlock Cat', image: '/images/ai/wyrlock-cat.webp', width: 1000, height: 667, featured: true },
  { id: 'district-10-love', title: 'District 10 Love', image: '/images/ai/district-10-love.webp', width: 563, height: 1000, featured: true },
  { id: 'gold-island', title: 'Gold Island', image: '/images/ai/gold-island.webp', width: 671, height: 1000, featured: true },
  { id: 'dystopia', title: 'Dystopia', image: '/images/ai/dystopia.webp', width: 1000, height: 667, featured: true },
  { id: 'sitting-on-the-throne', title: 'Sitting on the Throne', image: '/images/ai/sitting-on-the-throne.webp', width: 671, height: 1000, featured: true },
  { id: 'aliens-in-human-form-1', title: 'Aliens in Human Form', image: '/images/ai/aliens-in-human-form-1.webp', width: 671, height: 1000, featured: true },
  { id: 'mitsubishi-1', title: 'Mitsubishi I', image: '/images/ai/mitsubishi-1.webp', width: 1000, height: 545, featured: true },
  { id: 'mitsubishi-2', title: 'Mitsubishi II', image: '/images/ai/mitsubishi-2.webp', width: 1000, height: 546, featured: true },
  { id: 'concept-supercar', title: 'Concept Supercar', image: '/images/ai/concept-supercar.webp', width: 1000, height: 545, featured: true },
  { id: 'head-on-fire', title: 'Head on Fire', image: '/images/ai/head-on-fire.webp', width: 667, height: 1000, featured: true },
];

// Empty until there is moving work to show, and the site is built for that:
// the Videos tab falls back to its own empty state, and the wall simply has no
// clips in it. Adding one here is all that is needed to turn both on.
export const aiClips: AiClip[] = [];

// The three strands of the AI work. Each card opens a detail panel, so the
// `detail` block is what actually fills the section — replace the placeholder
// copy and examples with your own.
export interface AiDiscipline {
  id: string;
  label: string;
  description: string;
  // A representative piece. The visual strands lead with the work rather than a
  // written panel — the section already carries a generations strip and a full
  // gallery, so describing imagery in prose competes with the evidence sitting
  // directly beneath it. Cards with an image link into the gallery.
  image?: string;
  // Looping clip for a strand whose work moves. Shown in place of the still,
  // which stays on as the poster so there is something to look at before the
  // video has buffered.
  video?: string;
  // Where the card leads. Defaults to the gallery; the prompts strand sends
  // people to the library instead.
  href?: string;
  // Behind a request rather than open. The card explains itself instead of
  // navigating; clearing this opens the route up, with nothing else to change.
  locked?: boolean;
}

export const aiDisciplines: AiDiscipline[] = [
  {
    id: 'video',
    label: 'Video Generation',
    description: 'Short-form and cinematic sequences built shot by shot.',
    // The clip itself, with a real frame from it as the poster — a card
    // claiming video work should show video work, not a substituted still.
    image: '/images/ai/burning-head-poster.webp',
    video: '/images/ai/burning-head.mp4',
    href: '/explore?tab=videos',
  },
  {
    id: 'image',
    label: 'Image Generation',
    description: 'Concepts, characters, and stylised visuals.',
    image: '/images/ai/gold-island.webp',
  },
  {
    id: 'prompting',
    label: 'Prompts & Character Sheets',
    description: 'Prompt architecture, and the characters they were written for.',
    // Points at /library rather than opening a panel: the sheets and the prompt
    // bodies are both long-form, and a modal is the wrong container for either.
    href: '/library',
    locked: true,
  },
];

// Get top projects sorted by priority
// Featured set for the home page. Copies before sorting — the previous
// version sorted `projects` in place, mutating the shared exported array as a
// side effect of reading from it.
export const getTopProjects = (limit: number = 10) => {
  return [...projects]
    .filter((project) => project.spotlight !== false)
    .sort((a, b) => b.priority - a.priority)
    .slice(0, limit);
};

// Everything, for the full /projects listing. Array.prototype.sort is stable,
// so equal priorities keep their declaration order and the live work above
// stays at the top.
export const getAllProjects = () => {
  return [...projects].sort((a, b) => b.priority - a.priority);
}; 

// ---------------------------------------------------------------------------
// Library: character sheets and system prompts.
//
// Everything here is fiction. The characters are invented for the generated
// work, not people, and the sheets are the reference used to keep them
// consistent across renders.
// ---------------------------------------------------------------------------

// One beat of a character's story, tied to the picture that should be on screen
// while it is being read.
export interface CharacterChapter {
  id: string;
  // Shown while this beat is in view. A chapter without one keeps whatever was
  // already showing, so only the beats that change the picture need an image.
  image?: string;
  // Names this beat in the image controls, e.g. 'The kingdom'. Also the heading
  // above the text when present.
  label?: string;
  text: string;
}

export interface CharacterSheet {
  id: string;
  name: string;
  // Portrait for the grid card, and the opening picture in the detail view.
  image?: string;
  // One line for the card. The long version lives in `chapters`.
  summary?: string;
  // Short factual tags — origin, allegiance, weapon, whatever the sheet needs.
  traits?: string[];
  // The backstory, in order. Written as chapters rather than one block so the
  // picture can follow the reading: reach the part about their kingdom and the
  // kingdom is what you are looking at. A story with nothing to illustrate is
  // simply one chapter with no image.
  chapters?: CharacterChapter[];
}

export type PromptCategory = 'image' | 'video' | 'coding' | 'writing';

// Prompts divide before they divide by category. A system prompt sets up a
// model; a generative one asks for a picture or a clip. They are read for
// different reasons and the library keeps them apart, so the split is named
// here rather than inferred from a list of categories in a component.
export type PromptGroup = 'system' | 'generative';

// The pictures a generative prompt was fed. Optional, and most have none.
//
// The dimensions are carried so the strip across the top of the card can hold
// each picture's shape before any of the files arrive, which is the difference
// between a card that settles and one that jumps.
export interface PromptAsset {
  url: string;
  width?: number;
  height?: number;
}

export interface SystemPrompt {
  id: string;
  title: string;
  category: PromptCategory;
  // What it is for, in a sentence. Read before anyone decides to read the
  // prompt itself.
  purpose: string;
  // The prompt as written, shown in monospace and copyable.
  body: string;
  // What went in, for a prompt that took pictures as well as words. Shown as a
  // row above the body. Never on a system prompt, which has nothing to show.
  assets?: PromptAsset[];
}

// Category order and labels live here so adding one is a data change, not a
// component change.
export const promptCategories: {
  id: PromptCategory;
  label: string;
  group: PromptGroup;
}[] = [
  { id: 'image', label: 'Image', group: 'generative' },
  { id: 'video', label: 'Video', group: 'generative' },
  { id: 'coding', label: 'Coding', group: 'system' },
  { id: 'writing', label: 'Writing', group: 'system' },
];

// A category the list does not know about is a system prompt. That is the safer
// default: a generative tab showing something with no pictures reads as a bug,
// where a system prompt among system prompts simply reads as one more.
export const groupOf = (category: PromptCategory | string): PromptGroup =>
  promptCategories.find((c) => c.id === category)?.group ?? 'system';

// Seeded from renders that already exist, so every sheet has a real portrait.
// `chapters`, `summary` and `traits` are deliberately unwritten — that is the
// fiction, and it is yours. The page hides whatever is missing rather than
// showing a placeholder.
export const characterSheets: CharacterSheet[] = [
  // STUB STORY — placeholder prose, written only so the scroll-linked picture
  // behaviour can be seen working. Replace the text with the real thing; the
  // shape of it is what matters. Every other sheet below is still unwritten.
  {
    id: 'wyrlock-wirebound',
    name: 'Wyrlock Wirebound',
    image: '/images/ai/wyrlock-wirebound.webp',
    summary: 'Stub story, for testing the reader.',
    traits: ['Stub', 'Wirebound'],
    chapters: [
      {
        id: 'wyrlock-1',
        label: 'The wirebound',
        image: '/images/ai/wyrlock-wirebound.webp',
        text: 'They found him already wired, which is the part nobody agrees on. Some say the wire grew into him where he lay; others that he asked for it, and paid. What is not disputed is that by the time he could stand, the wire had opinions of its own, and that he learned to hold still while it decided things. He grew up in a valley where nothing else moved after dark. He learned the names of the things that did.',
      },
      {
        id: 'wyrlock-2',
        label: 'The kingdom',
        image: '/images/ai/gold-island.webp',
        text: 'The kingdom hung above the cloud line, gold and unreachable, and it had been unreachable so long that the people below had stopped looking up out of habit rather than despair. He looked up. That was the whole of his crime, in the end. There were roads to it once, they said, and the roads had been taken apart deliberately by someone who understood exactly how badly they would be wanted later.',
      },
      {
        id: 'wyrlock-3',
        label: 'The throne',
        image: '/images/ai/sitting-on-the-throne.webp',
        text: 'What waited on the throne had not moved in an age, and did not move when he arrived, and did not move while he said the thing he had come a long way to say. He had expected an argument. He had rehearsed one. Instead there was only the sound of the wire settling against itself, and a long silence in which he understood that being right had never been the difficult part.',
      },
      {
        id: 'wyrlock-4',
        label: 'After',
        image: '/images/ai/dystopia.webp',
        text: 'What was left of the valley did not remember his name, which he would have counted as a mercy. The wire went on without him, as it had gone on before him, threading quietly through whoever came next. Somewhere above the cloud line the gold is presumably still there. Nobody has been up to check in a long time, and the ones who talk about going tend to stop talking about it eventually.',
      },
    ],
  },
  { id: 'dune-woman', name: 'Dune Woman', image: '/images/ai/dune-woman.webp' },
  { id: 'woman-gold', name: 'Woman in Gold', image: '/images/ai/woman-gold.webp' },
  { id: 'throne', name: 'Sitting on the Throne', image: '/images/ai/sitting-on-the-throne.webp' },
  { id: 'aliens', name: 'Aliens in Human Form', image: '/images/ai/aliens-in-human-form-1.webp' },
  { id: 'ethereal', name: 'Ethereal', image: '/images/ai/ethereal.webp' },
];

// STUB PROMPTS — placeholder bodies, here only so the filtering, the layout and
// the copy button can be seen working. These are not yours and should not ship:
// a prompt is the one artefact on this page that cannot be written on your
// behalf. Delete these and the page falls back to its empty state.
//
// Bodies are template literals so the line breaks are real characters rather
// than escapes, which is how they are meant to be read and copied.
export const systemPrompts: SystemPrompt[] = [
  {
    id: 'stub-character-lock',
    title: 'Character lock',
    category: 'image',
    purpose: 'Holds one face across a set so a character stays recognisable between renders.',
    body: `ROLE
You are generating a consistent character across multiple images.

FIXED (never vary)
- Face structure, eye colour, hair length
- Scars, marks, jewellery

FREE (vary per shot)
- Pose, expression, camera angle, lighting

OUTPUT
One subject, centred, no text, no watermark.`,
  },
  {
    id: 'stub-shot-brief',
    title: 'Shot brief',
    category: 'video',
    purpose: 'Turns a still into a directed clip instead of generic drift.',
    body: `SHOT
Slow dolly-in, 35mm, shallow depth of field.

MOTION
Subject turns to camera on beat 3. Everything else settles.

HOLD
Final frame steady for 12 frames, for the cut.

AVOID
Camera shake, lens flare, background crowd, morphing hands.`,
  },
  {
    id: 'stub-review',
    title: 'Diff reviewer',
    category: 'coding',
    purpose: 'Reviews a change for defects rather than style opinions.',
    body: `You are reviewing a diff. Report only defects that would change
behaviour: correctness, data loss, races, unhandled failure.

For each finding give: the file and line, the concrete input that
triggers it, and what goes wrong.

Do not comment on formatting, naming, or preference.
If nothing qualifies, say so plainly.`,
  },
];
